import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Chip,
  Select,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import ConfirmDeleteDialog from '../components/dialogs/ConfirmDeleteDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  AddCircleOutline as AddRowIcon,
} from '@mui/icons-material';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import { useForm, Controller, useFieldArray } from 'react-hook-form';

import { taskRuleService } from '../services/taskRuleService';
import { TaskRule, TaskRuleCreate, TaskRuleContext, TaskRuleArea, TaskRuleSeverity, TaskRuleAction, TaskRuleOwner } from '../types/taskRule';
import { CriteriaItem } from '../types/taskRule';
import { NotificationEntity } from '../services/patientNotificationService';
import { meetingTypeService } from '../services/meetingTypeService';
import { userService } from '../services/userService';
import { clinicService } from '../services/clinicService';
import CriteriaRow from '../components/criteria/CriteriaRow';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import RequirePermission from '../components/common/RequirePermission';
import { useCrudHandlers } from '../hooks/useCrudHandlers';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { taskRuleColumns } from '../config/taskRuleColumns';
import patientStatusConfigService from '../services/patientStatusConfigService';
import { usePatientFieldConfigs } from '../hooks/usePatientFieldConfigs';

const PATIENT_DATE_FIELDS = [
  { value: 'scheduleMeetingStart', label: 'Schedule Meeting Start' },
  { value: 'nextMeetingStart', label: 'Next Meeting Start' },
  { value: 'createdAt', label: 'Created At' },
  { value: 'updatedAt', label: 'Updated At' },
];

const MEETING_DATE_FIELDS = [
  { value: 'date', label: 'Meeting Date' },
];

const entityForCriteria = (context: TaskRuleContext): NotificationEntity => {
  return context === 'PATIENT' ? NotificationEntity.PATIENT : NotificationEntity.MEETING;
};

const ACTION_OPTIONS: { value: TaskRuleAction; label: string }[] = [
  { value: 'OPEN_PATIENT', label: 'Open Patient' },
  { value: 'OPEN_MEETING', label: 'Open Meeting' },
  { value: 'OPEN_SUMMARY', label: 'Open Summary' },
  { value: 'APPROVE_TITRATION_MEASUREMENTS', label: 'Approve Titration Measurements' },
  { value: 'MEDICATION_MONITORING_ISSUE', label: 'Medication Monitoring Issue' },
];

const OWNER_OPTIONS: { value: TaskRuleOwner; label: string }[] = [
  { value: 'DOCTOR', label: 'Doctor' },
  { value: 'CASE_MANAGER', label: 'Case Manager' },
  { value: 'NURSE_PRESCRIBER', label: 'Nurse Prescriber' },
  { value: 'PRESCRIBER', label: 'Prescribing Clinician' },
];

interface FormValues {
  name: string;
  task_type: string;
  area: TaskRuleArea;
  severity: TaskRuleSeverity;
  context: TaskRuleContext;
  date_field?: string | null;
  action?: TaskRuleAction | null;
  task_owner?: TaskRuleOwner | null;
  escalate_enabled: boolean;
  escalate_after_days: number;
  is_active: boolean;
  include_criteria_fields: CriteriaItem[];
  exclude_criteria_fields: CriteriaItem[];
}

const emptyCriteriaItem = (): CriteriaItem => ({ field: '', operator: 'equals', value: '' });

const TaskRulesPage: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const { dynamicFieldMeta } = usePatientFieldConfigs();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<TaskRule | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<TaskRule | null>(null);
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, { id: number | string; name: string }[]>>({});

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      context: 'PATIENT',
      area: 'CLINIC',
      severity: 'WARNING',
      action: null,
      task_owner: null,
      escalate_enabled: false,
      escalate_after_days: 7,
      is_active: true,
      include_criteria_fields: [],
      exclude_criteria_fields: [],
    },
  });

  const {
    fields: includeFields,
    append: appendInclude,
    remove: removeInclude,
  } = useFieldArray({ control, name: 'include_criteria_fields' });

  const {
    fields: excludeFields,
    append: appendExclude,
    remove: removeExclude,
  } = useFieldArray({ control, name: 'exclude_criteria_fields' });

  const watchedContext = watch('context');
  const watchedArea = watch('area');
  const showOwnerSelect = watchedContext === 'PATIENT' && watchedArea === 'CLINIC';
  const watchedSeverity = watch('severity');
  const watchedEscalateEnabled = watch('escalate_enabled');

  const dateFieldOptions = watchedContext === 'MEETING' ? MEETING_DATE_FIELDS : PATIENT_DATE_FIELDS;

  // Load dynamic options for criteria dropdowns when dialog opens
  useEffect(() => {
    if (!dialogOpen || !currentTenant) return;
    const loadDynamicOptions = async () => {
      try {
        const [meetingTypes, users, patientStatuses, clinics] = await Promise.all([
          meetingTypeService.getMeetingTypes(currentTenant.id),
          userService.getUsers(currentTenant.id),
          patientStatusConfigService.getAll(currentTenant.id),
          clinicService.getClinics(currentTenant.id),
        ]);
        setDynamicOptions({
          meetingTypes: meetingTypes.map((t) => ({ id: t.id, name: t.name })),
          users: users.map((u) => ({ id: u.id, name: u.full_name })),
          patientStatuses: patientStatuses.map((s) => ({ id: s.key, name: s.title })),
          clinics: clinics.map((c) => ({ id: c.id, name: c.name })),
        });
      } catch (err) {
        console.error('Failed to load dynamic criteria options:', err);
      }
    };
    loadDynamicOptions();
  }, [dialogOpen, currentTenant]);

  const getSortValue = useCallback((rule: TaskRule, key: string): any => {
    switch (key) {
      case 'name': return rule.name.toLowerCase();
      case 'taskType': return rule.task_type.toLowerCase();
      case 'context': return rule.context.toLowerCase();
      case 'area': return rule.area.toLowerCase();
      case 'severity': return rule.severity.toLowerCase();
      case 'dateField': return rule.date_field ?? '';
      case 'isActive': return rule.is_active ? 1 : 0;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((rule: TaskRule): string[] => {
    return [
      rule.name,
      rule.task_type,
      rule.context,
      rule.area,
      rule.severity,
      rule.date_field ?? '',
    ];
  }, []);

  const table = useTablePage<TaskRule>({
    entityType: 'taskRules',
    columns: taskRuleColumns,
    tenantId: currentTenant?.id,
    getSortValue,
    getSearchText,
  });

  const loadRules = useCallback(async () => {
    if (!currentTenant) return;
    table.loadData(() => taskRuleService.getAll(currentTenant.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant]);

  useEffect(() => {
    if (currentTenant) {
      loadRules();
    }
  }, [currentTenant, loadRules]);

  const handleOpenDialog = (rule?: TaskRule) => {
    if (rule) {
      setEditingRule(rule);
      setValue('name', rule.name);
      setValue('task_type', rule.task_type);
      setValue('area', rule.area);
      setValue('severity', rule.severity);
      setValue('context', rule.context);
      setValue('date_field', rule.date_field ?? null);
      setValue('action', rule.action ?? null);
      setValue('task_owner', rule.task_owner ?? null);
      setValue('escalate_enabled', rule.escalate_after_days != null);
      setValue('escalate_after_days', rule.escalate_after_days ?? 7);
      setValue('is_active', rule.is_active ?? true);
      setValue('include_criteria_fields', rule.include_criteria ?? []);
      setValue('exclude_criteria_fields', rule.exclude_criteria ?? []);
    } else {
      setEditingRule(null);
      reset({
        context: 'PATIENT',
        area: 'CLINIC',
        severity: 'WARNING',
        action: null,
        task_owner: null,
        escalate_enabled: false,
        escalate_after_days: 7,
        is_active: true,
        include_criteria_fields: [],
        exclude_criteria_fields: [],
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRule(null);
    reset();
  };

  const crud = useCrudHandlers<TaskRule>({
    table,
    createFn: (data) => taskRuleService.create(data),
    updateFn: (id, data) => taskRuleService.update(id as number, data),
    deleteFn: (id) => taskRuleService.delete(id as number),
    entityName: 'Task rule',
    onSuccess: () => { handleCloseDialog(); },
    onDeleteSuccess: () => { setDeleteDialogOpen(false); setRuleToDelete(null); },
    reloadData: loadRules,
  });

  const onSubmit = (formData: FormValues) => {
    if (!currentTenant) return;

    const payload: TaskRuleCreate = {
      name: formData.name,
      task_type: formData.task_type,
      area: formData.area,
      severity: formData.severity,
      context: formData.context,
      date_field: formData.date_field || null,
      action: formData.action || null,
      // Owner only applies to PATIENT + CLINIC rules; clear it otherwise.
      task_owner: (formData.context === 'PATIENT' && formData.area === 'CLINIC')
        ? (formData.task_owner || null)
        : null,
      escalate_after_days: (formData.escalate_enabled && formData.severity === 'WARNING')
        ? (formData.escalate_after_days ?? 7)
        : null,
      include_criteria: formData.include_criteria_fields?.filter(c => c.field) ?? [],
      exclude_criteria: formData.exclude_criteria_fields?.filter(c => c.field) ?? [],
      is_active: formData.is_active ?? true,
      tenant_id: currentTenant.id,
    };

    crud.handleCreateOrUpdate(payload, editingRule);
  };

  const handleDelete = () => {
    crud.handleDelete(ruleToDelete);
  };

  const getColumnValue = (rule: TaskRule, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'name':
        return (
          <Box display="flex" alignItems="center">
            <AssignmentIcon sx={{ mr: 2, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight="medium">{rule.name}</Typography>
          </Box>
        );
      case 'taskType':
        return <Typography variant="body2">{rule.task_type}</Typography>;
      case 'context':
        return (
          <Chip
            label={rule.context === 'PATIENT' ? 'Patient' : 'Meeting'}
            color={rule.context === 'PATIENT' ? 'primary' : 'success'}
            size="small"
          />
        );
      case 'area':
        return (
          <Typography variant="body2">
            {rule.area === 'CLINIC' ? 'Clinic' : 'Admin'}
          </Typography>
        );
      case 'severity':
        return (
          <Chip
            label={rule.severity}
            color={rule.severity === 'ERROR' ? 'error' : 'warning'}
            size="small"
          />
        );
      case 'dateField':
        return (
          <Typography variant="body2" color={rule.date_field ? 'text.primary' : 'text.secondary'}>
            {rule.date_field || '—'}
          </Typography>
        );
      case 'isActive':
        return (
          <Chip
            label={rule.is_active ? 'Active' : 'Inactive'}
            color={rule.is_active ? 'success' : 'default'}
            size="small"
          />
        );
      default:
        return '';
    }
  };

  const getContextMenuOptions = (): ContextMenuOption[] => {
    if (!table.selectedItem) return [];
    return [
      {
        label: 'Edit',
        icon: <EditIcon sx={{ mr: 2 }} />,
        onClick: () => {
          handleOpenDialog(table.selectedItem!);
          table.handleMenuClose();
        },
      },
      {
        label: 'Delete',
        icon: <DeleteIcon sx={{ mr: 2 }} />,
        onClick: () => {
          setRuleToDelete(table.selectedItem!);
          setDeleteDialogOpen(true);
          table.handleMenuClose();
        },
        color: 'error' as const,
      },
    ];
  };

  const renderMobileCard = (rule: TaskRule) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} sx={{ pr: 5 }}>
        <Box display="flex" alignItems="center">
          <AssignmentIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
            {rule.name}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Chip
            label={rule.context === 'PATIENT' ? 'Patient' : 'Meeting'}
            color={rule.context === 'PATIENT' ? 'primary' : 'success'}
            size="small"
          />
          <Chip
            label={rule.is_active ? 'Active' : 'Inactive'}
            color={rule.is_active ? 'success' : 'default'}
            size="small"
          />
        </Box>
      </Box>
      <Box display="flex" gap={1} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          Type: {rule.task_type}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Area: {rule.area === 'CLINIC' ? 'Clinic' : 'Admin'}
        </Typography>
        <Chip label={rule.severity} color={rule.severity === 'ERROR' ? 'error' : 'warning'} size="small" />
      </Box>
    </>
  );

  return (
    <RequirePermission check={!!user?.user_privilege?.config} featureName="task rules">
      <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading task rules:">
        <DataTableToolbar
          {...table.toolbarProps}
          title="Task Rules"
          searchPlaceholder="Search task rules by name, type, or context..."
          actions={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Add Rule
            </Button>
          }
        />

        {isMobile ? (
          <MobileCardGrid
            data={table.sortedData}
            renderCard={renderMobileCard}
            getItemKey={(r) => r.id}
            onMenuClick={table.handleRowClick}
            emptyIcon={<AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
            emptyMessage="No task rules found"
          />
        ) : (
          <DataTableContainer {...table.containerProps}>
            <DataTableBody
              data={table.sortedData}
              {...table.tableBodyProps}
              getColumnValue={getColumnValue}
              getRowKey={(r) => r.id}
              onRowClick={table.handleRowClick}
            />
          </DataTableContainer>
        )}

        <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

        {/* Create/Edit Dialog */}
        <ClinicDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          title={editingRule ? 'Edit Task Rule' : 'Create Task Rule'}
          onSubmit={handleSubmit(onSubmit)}
          maxWidth="md"
          actions={
            <>
              <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
                {editingRule ? 'Update' : 'Create'}
              </Button>
              <Button onClick={handleCloseDialog} sx={CLINIC_DIALOG_SX.cancelButton}>
                Cancel
              </Button>
            </>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Name */}
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Name</Typography>
              <TextField
                {...register('name', { required: 'Name is required' })}
                fullWidth
                size="small"
                error={!!errors.name}
                helperText={errors.name?.message}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            {/* Task Type + Context (two columns) */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ ...CLINIC_DIALOG_SX.fieldLabel, display: 'block', mb: 0.5 }}>Task Type</Typography>
                <TextField
                  {...register('task_type', { required: 'Task type is required' })}
                  fullWidth
                  size="small"
                  error={!!errors.task_type}
                  helperText={errors.task_type?.message}
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ ...CLINIC_DIALOG_SX.fieldLabel, display: 'block', mb: 0.5 }}>Context</Typography>
                <Controller
                  name="context"
                  control={control}
                  rules={{ required: 'Context is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      fullWidth
                      size="small"
                      sx={CLINIC_DIALOG_SX.select}
                      onChange={(e) => {
                        field.onChange(e);
                        // Reset criteria and date_field when context changes
                        setValue('include_criteria_fields', []);
                        setValue('exclude_criteria_fields', []);
                        setValue('date_field', null);
                      }}
                    >
                      <MenuItem value="PATIENT">Patient</MenuItem>
                      <MenuItem value="MEETING">Meeting</MenuItem>
                    </Select>
                  )}
                />
              </Box>
            </Box>

            {/* Area + Severity (two columns) */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ ...CLINIC_DIALOG_SX.fieldLabel, display: 'block', mb: 0.5 }}>Area</Typography>
                <Controller
                  name="area"
                  control={control}
                  rules={{ required: 'Area is required' }}
                  render={({ field }) => (
                    <Select {...field} fullWidth size="small" sx={CLINIC_DIALOG_SX.select}>
                      <MenuItem value="CLINIC">Clinic</MenuItem>
                      <MenuItem value="ADMIN">Admin</MenuItem>
                    </Select>
                  )}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ ...CLINIC_DIALOG_SX.fieldLabel, display: 'block', mb: 0.5 }}>Severity</Typography>
                <Controller
                  name="severity"
                  control={control}
                  rules={{ required: 'Severity is required' }}
                  render={({ field }) => (
                    <Select {...field} fullWidth size="small" sx={CLINIC_DIALOG_SX.select}>
                      <MenuItem value="WARNING">Warning</MenuItem>
                      <MenuItem value="ERROR">Error</MenuItem>
                    </Select>
                  )}
                />
              </Box>
            </Box>

            {/* Date Field */}
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Date Field</Typography>
              <Controller
                name="date_field"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    size="small"
                    sx={CLINIC_DIALOG_SX.select}
                    displayEmpty
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {dateFieldOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Box>

            {/* Escalation — only relevant when severity is WARNING */}
            {watchedSeverity === 'WARNING' && (
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Escalate to Error</Typography>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <Controller
                    name="escalate_enabled"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="After X days"
                        sx={{ m: 0 }}
                      />
                    )}
                  />
                  {watchedEscalateEnabled && (
                    <Controller
                      name="escalate_after_days"
                      control={control}
                      rules={{ min: 1 }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          size="small"
                          label="Days"
                          inputProps={{ min: 1 }}
                          sx={{ width: 100 }}
                          onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 1)}
                        />
                      )}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* Action */}
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Action</Typography>
              <Controller
                name="action"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    size="small"
                    sx={CLINIC_DIALOG_SX.select}
                    displayEmpty
                  >
                    <MenuItem value=""><em>Open Patient (default)</em></MenuItem>
                    {ACTION_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Box>

            {/* Task Owner (PATIENT + CLINIC only) */}
            {showOwnerSelect && (
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Owner</Typography>
                <Controller
                  name="task_owner"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value ?? ''}
                      fullWidth
                      size="small"
                      sx={CLINIC_DIALOG_SX.select}
                      displayEmpty
                    >
                      <MenuItem value=""><em>Unassigned (Secretary)</em></MenuItem>
                      {OWNER_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Box>
            )}

            {/* Is Active */}
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Active</Typography>
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={field.value ? 'Active' : 'Inactive'}
                  />
                )}
              />
            </Box>

            {/* Include Criteria */}
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="body2" sx={{ color: CLINIC_DIALOG_SX.fieldLabel.color, fontWeight: 500 }}>
                  Include Criteria (all must match)
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddRowIcon />}
                  onClick={() => appendInclude(emptyCriteriaItem())}
                  sx={{ textTransform: 'none', color: CLINIC_DIALOG_SX.fieldLabel.color }}
                >
                  Add Condition
                </Button>
              </Box>
              {includeFields.map((field, index) => (
                <CriteriaRow
                  key={field.id}
                  index={index}
                  prefix="include_criteria_fields"
                  onRemove={removeInclude}
                  entity={entityForCriteria(watchedContext)}
                  control={control}
                  setValue={setValue}
                  dynamicOptions={dynamicOptions}
                  extraFields={watchedContext === 'PATIENT' ? dynamicFieldMeta : []}
                />
              ))}
              {includeFields.length === 0 && (
                <Typography variant="caption" color="text.secondary">No include rules — all records match.</Typography>
              )}
            </Box>

            {/* Exclude Criteria */}
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="body2" sx={{ color: CLINIC_DIALOG_SX.fieldLabel.color, fontWeight: 500 }}>
                  Exclude Criteria (any match excludes)
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddRowIcon />}
                  onClick={() => appendExclude(emptyCriteriaItem())}
                  sx={{ textTransform: 'none', color: CLINIC_DIALOG_SX.fieldLabel.color }}
                >
                  Add Condition
                </Button>
              </Box>
              {excludeFields.map((field, index) => (
                <CriteriaRow
                  key={field.id}
                  index={index}
                  prefix="exclude_criteria_fields"
                  onRemove={removeExclude}
                  entity={entityForCriteria(watchedContext)}
                  control={control}
                  setValue={setValue}
                  dynamicOptions={dynamicOptions}
                  extraFields={watchedContext === 'PATIENT' ? dynamicFieldMeta : []}
                />
              ))}
              {excludeFields.length === 0 && (
                <Typography variant="caption" color="text.secondary">No exclude rules — no records are excluded.</Typography>
              )}
            </Box>
          </Box>
        </ClinicDialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDelete}
          entityType="Task Rule"
          entityName={ruleToDelete?.name}
        />

        <ManageColumnsModal {...table.manageColumnsProps} />
        <SaveViewModal {...table.saveViewModalProps} />
      </TablePageLayout>
    </RequirePermission>
  );
};

export default TaskRulesPage;
